import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, computed, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { IFormsProvider, LiveForm, SAVE_FORM_RESULT, UserInformation } from '@lfw/roesberg.liveforms.core';
import { LiveformsEditorComponent } from '@lfw/roesberg.liveforms.editor';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EMPTY, firstValueFrom, Observable, of, take } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { NetworkService, NetworkState } from '../../../network.service';
import { LfwRouterService } from '../../../route/lfw-router.service';
import { CanComponentDeactivate } from '../../../shared/component-based-candeactivate.guard';
import { AppToolbarButtonComponent } from '../../../shared/components/app-toolbar-button/app-toolbar-button.component';
import { AppToolbarDividerComponent } from '../../../shared/components/app-toolbar-divider/app-toolbar-divider.component';
import { AppToolbarTextComponent } from '../../../shared/components/app-toolbar-text/app-toolbar-text.component';
import { ConfirmDialogComponent } from '../../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from '../../../shared/dialogs/error-dialog/error-dialog.component';
import { FormCloseErrorDialogComponent } from '../../../shared/dialogs/form-close-error-dialog/form-close-error-dialog.component';
import {
  SAVE_CHANGES_RESULT,
  SaveChangesDialogComponent
} from '../../../shared/dialogs/save-changes-dialog/save-changes-dialog.component';
import { BusySnackbarService } from '../../../shared/services/busy-snackbar.service';
import { DefaultDialogService } from '../../../shared/services/default-dialog.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { InfoService } from '../../../shared/services/info.service';
import { SettingsService } from '../../../shared/settings/settings.service';
import { MenuService } from '../../../shell/menu.service';
import { UserInformationService } from '../../../shell/user-information.service';
import { FormEditToolbarComponent } from '../../components/form-toolbar/form-edit-toolbar.component';
import { OrderEditorMobileMenuComponent } from '../../components/order-editor-mobile-menu/order-editor-mobile-menu.component';
import { SubStateSelectComponent } from '../../components/sub-state-select/sub-state-select.component';
import { OrdersService } from '../../orders.service';
import { OnlineFormsProvider } from '../online-forms.provider';

@Component({
  selector: 'lfw-online-form-view',
  templateUrl: './online-order-editor.component.html',
  styleUrl: './online-order-editor.component.css',
  providers: [{ provide: IFormsProvider, useClass: OnlineFormsProvider }],
  imports: [
    LiveformsEditorComponent,
    FormEditToolbarComponent,
    AppToolbarButtonComponent,
    AppToolbarDividerComponent,
    AppToolbarTextComponent,
    SubStateSelectComponent,
    TranslatePipe,
    OrderEditorMobileMenuComponent
  ]
})
export class OnlineOrderEditorComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly dialog = inject(MatDialog);
  private readonly busySnackbar = inject(BusySnackbarService);
  private readonly userInfoService = inject(UserInformationService);
  private readonly routerService = inject(LfwRouterService);
  private readonly translate = inject(TranslateService);
  private readonly infoService = inject(InfoService);
  private readonly orderService = inject(OrdersService);
  private readonly dialogService = inject(DefaultDialogService);
  private readonly errorHandling = inject(ErrorHandlerService);
  private readonly menuService = inject(MenuService);
  private readonly title = inject(Title);
  private readonly networkService = inject(NetworkService);
  private readonly settingsService = inject(SettingsService);

  form: LiveForm | undefined = undefined;
  user: UserInformation = this.userInfoService.userInformation;
  bypassCanDeactivate = false;
  private readonly previouslyDisabledControls = new Set<string>();

  readonly editor = viewChild.required(LiveformsEditorComponent);

  readonly changedForm = signal(false);
  readonly isSaving = signal(false);
  readonly isFinalizing = signal(false);
  readonly isClosing = signal(false);

  readonly isBusy = computed(() => this.isSaving() || this.isFinalizing() || this.isClosing());
  readonly showOverlay = computed(() => this.isFinalizing() || this.isClosing());

  readonly canFinalize = toSignal(this.networkService.state$.pipe(map((state: NetworkState) => state === 'online')), {
    initialValue: navigator.onLine
  });

  formsProvider = inject(IFormsProvider);

  editorSettings = this.settingsService.getEditorSettings();

  private readonly offlineTooltip = toSignal(
    this.translate.stream('ORDERS.ACTION_TOOLTIP_NOT_POSSIBLE_WHILE_OFFLINE'),
    { initialValue: this.translate.instant('ORDERS.ACTION_TOOLTIP_NOT_POSSIBLE_WHILE_OFFLINE') }
  );

  readonly tooltipForFinalizeButton = computed(() => (this.canFinalize() ? undefined : this.offlineTooltip()));

  // Asks the browser for its "leave site?" prompt while an order is locked, so a user closing the tab
  // gets the chance to go back and leave properly via the close button, which unlocks the order.
  //
  // It deliberately does NOT try to unlock here: preventDefault() cannot report whether the user
  // confirmed, so unlocking now would release the order even when they choose to stay, leaving them
  // editing an order they no longer hold. Moving the unlock to 'pagehide' was rejected too, because
  // iOS fires that when a PWA is merely backgrounded. A lock that outlives the tab is recovered by
  // reopening the order (FORMOWNLOCK), which is far cheaper than losing someone's work.
  //
  // Arrow property so `this` is the component and so the same reference can be removed again.
  private readonly unload = (event: BeforeUnloadEvent): void => {
    if (!this.form || this.isBusy()) {
      return;
    }
    event.preventDefault();
  };

  constructor() {
    this.menuService.hideNavigation();
  }

  ngOnDestroy(): void {
    this.menuService.showNavigation();
    window.removeEventListener('beforeunload', this.unload);
  }

  ngOnInit(): void {
    this.loadForm();
  }

  private loadForm(forceEdit = false): void {
    this.formsProvider
      .loadFormForEdit(forceEdit) // for testing, must be false later on
      .pipe(
        take(1),
        tap((form: LiveForm) => {
          this.form = form;
          this.title.setTitle(this.form.order.text);
          // The order is locked from here on, not from the first change, so the listener has to
          // exist for as long as the lock does. Removed again in ngOnDestroy.
          window.addEventListener('beforeunload', this.unload);
        }),
        catchError((httpError) => {
          this.handleLoadError(httpError);
          return of(httpError);
        })
      )
      .subscribe();
  }

  error(formId: string): void {
    this.dialogService.formNotFound(formId);
  }

  get caption(): string {
    return this.form?.order.text ?? '';
  }

  get viewVisible(): boolean {
    return true; // this.form != undefined && this.form.id != '';
  }

  canSave(): boolean {
    return this.changedForm();
  }

  save(): void {
    this.saveAndReportOutcome().subscribe();
  }

  /**
   * Saves the form and reports whether it worked, so closing can decide whether to go through with it.
   *
   * Always emits: a failure must surface as `false` rather than an empty stream, otherwise the
   * `canDeactivate` guard awaiting it would never resolve and navigation would hang silently. Note that
   * `SAVE_FORM_RESULT.FAILED` arrives *without* an error being thrown, so the result has to be checked
   * as well as the error channel.
   */
  private saveAndReportOutcome(): Observable<boolean> {
    // `isSaving` only - NOT `isBusy()`: closing has already set `isClosing`, and rejecting on that would
    // make saving-while-closing a silent no-op reported as a failed save.
    if (!this.form || this.isSaving() || this.isFinalizing()) {
      return of(false);
    }
    this.isSaving.set(true);
    this.busySnackbar.show('ORDERS.NOTIFICATIONS.SAVING_IN_PROGRESS');

    return this.editor()
      .save()
      .pipe(
        map((result) => {
          if (result === SAVE_FORM_RESULT.FAILED) {
            this.infoService.setStatusTranslate('FORM.DLG_SAVE_FAILED');
            return false;
          }
          this.editor().formGroup.markAsPristine();
          this.changedForm.set(false);
          return true;
        }),
        catchError((error) => {
          this.dialogService.showSaveError(error);
          return of(false);
        }),
        finalize(() => {
          this.isSaving.set(false);
        }),
        take(1)
      );
  }

  finalizeOrder(): void {
    if (!this.form || this.isBusy()) {
      return;
    }
    this.isFinalizing.set(true);
    this.disableFormGroup();
    const snackRef = this.busySnackbar.show('ORDERS.NOTIFICATIONS.FINALIZING_IN_PROGRESS');

    of(this.form)
      .pipe(
        switchMap(() =>
          this.editor()
            .save(this.translate.instant('DIALOGS.FINALIZE_NOT_POSSIBLE'))
            .pipe(filter((result) => result !== SAVE_FORM_RESULT.FAILED))
        ),
        switchMap((result) => {
          if (!this.form || result !== SAVE_FORM_RESULT.SAVED_ONLINE) {
            this.infoService.setStatusTranslate('DIALOGS.FINALIZE_NOT_POSSIBLE');
            return EMPTY;
          }
          this.editor().formGroup.markAsPristine();
          this.changedForm.set(false);
          return this.orderService.finishOrder(this.form.project.id, this.form.order.id).pipe(
            tap(() => {
              this.bypassCanDeactivate = true;
              this.routerService.navigateToOrdersRoot();
            })
          );
        }),
        catchError((error) => {
          this.dialogService.showSaveError(error);
          return EMPTY;
        }),
        finalize(() => {
          snackRef.dismiss();
          this.restoreFormGroup();
          this.isFinalizing.set(false);
        }),
        take(1)
      )
      .subscribe();
  }

  async close(): Promise<void> {
    if (this.isBusy()) {
      return;
    }
    this.isClosing.set(true);

    const networkState = await firstValueFrom(this.networkService.state$);

    if (networkState === 'offline') {
      this.bypassCanDeactivate = true;
      this.routerService.navigateToTemporaryOfflineOrdersOverview();
    } else {
      this.routerService.navigateToOrdersRoot();
    }
  }

  subStateSelectionChanged(): void {
    this.editor().formGroup.markAsDirty();
  }

  formChanged(): void {
    this.changedForm.set(this.editor().formGroup.dirty);
  }

  private handleLoadError(httpError: any): void {
    const errorCode = this.errorHandling.getErrorCode(httpError);
    const message = this.errorHandling.getErrorMessage(httpError);
    const title = this.translate.instant('DIALOGS.WARNING');
    this.dialog
      //TODO: This error code 'FORMOWNLOCK' comes directly from the backend and is currently this string. We should think about this and put such error codes into e.g. a static error class.
      .open(errorCode === 'FORMOWNLOCK' ? ConfirmDialogComponent : ErrorDialogComponent, {
        data: { title, message },
        closeOnNavigation: true,
        disableClose: true
      })
      .afterClosed()
      .pipe(
        take(1),
        tap((force) => {
          if (force) {
            this.loadForm(force);
          } else {
            this.close();
          }
        })
      )
      .subscribe();
  }

  canDeactivate(): Observable<boolean> {
    if (this.isBusy() && !this.isClosing() && !this.bypassCanDeactivate) {
      return of(false);
    }
    if (!this.form) {
      return of(true);
    }
    if (!this.changedForm()) {
      return this.lockAndPause();
    }
    return this.dialog
      .open(SaveChangesDialogComponent, {
        data: {
          title: 'DIALOGS.SAVE_CHANGES_TITLE',
          message: 'DIALOGS.SAVE_CHANGES_MESSAGE',
          params: { order: this.form.order.text }
        }
      })
      .afterClosed()
      .pipe(
        switchMap((result: SAVE_CHANGES_RESULT | undefined) => {
          if (!this.form) {
            return of(true);
          }
          if (result === SAVE_CHANGES_RESULT.DISCARD) {
            return this.lockAndPause();
          }
          if (result === SAVE_CHANGES_RESULT.SAVE) {
            return this.saveThenClose();
          }
          // Cancel, or the dialog dismissed without a choice.
          this.isClosing.set(false);
          return of(false);
        }),
        take(1)
      );
  }

  /**
   * Saves before releasing the order, and stays on it when the save fails.
   *
   * The order matters: saving does not touch the lock, while `lockAndPause` IS the unlock - so pausing
   * before a confirmed save could release an order whose changes were never stored.
   *
   * Deliberately unlike the desktop, which shows the error and closes anyway
   * (`FormProcessorViewModel.CloseExecute` runs `base.CloseExecute()` regardless of the save outcome).
   * Keeping the order open is what lets the user retry instead of losing the work.
   */
  private saveThenClose(): Observable<boolean> {
    return this.saveAndReportOutcome().pipe(
      switchMap((saved) => {
        if (!saved) {
          this.isClosing.set(false);
          return of(false);
        }
        return this.lockAndPause();
      })
    );
  }

  private lockAndPause(): Observable<boolean> {
    this.disableFormGroup();
    this.busySnackbar.show('ORDERS.NOTIFICATIONS.CLOSING_IN_PROGRESS');
    return this.orderService.pauseOrder(this.form!.project.id, this.form!.order.id).pipe(
      take(1),
      map(() => true),
      catchError((error) => this.handleCloseError(error))
    );
  }

  private handleCloseError(error: HttpErrorResponse): Observable<boolean> {
    const status = error.status;

    if (status === HttpStatusCode.NotFound) {
      return of(true);
    }

    const message = error.error?.detail ?? this.translate.instant('ERROR.UNKNOWN');

    return this.dialog
      .open(FormCloseErrorDialogComponent, {
        data: {
          title: this.translate.instant('DIALOGS.ERROR'),
          message
        },
        disableClose: true
      })
      .afterClosed()
      .pipe(
        map((forceExit: boolean) => {
          if (!forceExit) {
            this.isClosing.set(false);
            this.restoreFormGroup();
          }
          return forceExit === true;
        })
      );
  }

  private disableFormGroup(): void {
    const formGroup = this.editor().formGroup;
    this.previouslyDisabledControls.clear();
    this.collectDisabledPaths(formGroup, '');
    formGroup.disable();
  }

  private restoreFormGroup(): void {
    const formGroup = this.editor().formGroup;
    formGroup.enable();
    for (const path of this.previouslyDisabledControls) {
      formGroup.get(path)?.disable();
    }
  }

  private collectDisabledPaths(group: { controls: Record<string, any> }, prefix: string): void {
    for (const key of Object.keys(group.controls)) {
      const control = group.controls[key];
      const path = prefix ? `${prefix}.${key}` : key;
      if (control.disabled) {
        this.previouslyDisabledControls.add(path);
      } else if (control.controls) {
        this.collectDisabledPaths(control, path);
      }
    }
  }
}
